// ===========================================
// Task Brief Builder - Guided Wizard Component
// ===========================================
// Multi-step form that guides users through creating a structured Task Brief
// Based on the three-layer prompting framework for AI IDEs
//
// Steps:
// 1. Task - One-sentence goal
// 2. Context - @Files, @Folders, @Docs to attach
// 3. Behavior - Current vs desired behavior
// 4. Scope - In scope / out of scope
// 5. Constraints - Tech, style, safety constraints
// 6. Acceptance - Testable success criteria
// 7. Review - Review and generate final markdown

import React, { useState } from 'react';
import {
  Target,
  FileText,
  FolderOpen,
  BookOpen,
  ArrowRight,
  ArrowLeft,
  Plus,
  X,
  Check,
  Clipboard,
  ClipboardList,
  Shield,
  CheckSquare,
  Eye
} from 'lucide-react';
import './TaskBriefBuilder.scss';

// ===========================================
// Step Definitions
// ===========================================
const STEPS = [
  { id: 'task', label: 'Task', icon: Target, description: 'Define your goal' },
  { id: 'context', label: 'Context', icon: FolderOpen, description: 'What to attach' },
  { id: 'behavior', label: 'Behavior', icon: ArrowRight, description: 'Current → Desired' },
  { id: 'scope', label: 'Scope', icon: ClipboardList, description: 'In/Out of scope' },
  { id: 'constraints', label: 'Constraints', icon: Shield, description: 'Boundaries' },
  { id: 'acceptance', label: 'Acceptance', icon: CheckSquare, description: 'Success criteria' },
  { id: 'review', label: 'Review', icon: Eye, description: 'Generate brief' }
];

// ===========================================
// IDE Mode Options
// ===========================================
const IDE_MODES = [
  { value: 'any', label: 'Any Mode', description: 'No specific mode recommended' },
  { value: 'ask', label: 'Ask', description: 'Read-only exploration, no edits' },
  { value: 'plan', label: 'Plan', description: 'Plan first, then execute' },
  { value: 'agent', label: 'Agent', description: 'Autonomous edits + commands' },
  { value: 'debug', label: 'Debug', description: 'Hypotheses + instrumentation' }
];

// ===========================================
// Working Style Presets
// ===========================================
const WORKING_STYLE_PRESETS = [
  {
    label: 'Plan First',
    value: `1. First: restate understanding + list the files you will touch
2. Then: propose a step-by-step plan (no code yet)
3. Then: implement with minimal diffs
4. Then: show verification steps + what to run
5. End: summary of changes + follow-ups`
  },
  {
    label: 'Minimal Diffs',
    value: 'Make the smallest possible changes to achieve the goal. Avoid refactoring or cleanup outside the immediate scope.'
  },
  {
    label: 'Read Only',
    value: 'DO NOT EDIT FILES. This is a read-only analysis. Use Ask mode.'
  },
  {
    label: 'Explain First',
    value: 'Before making any changes, explain the root cause and your proposed solution. Wait for confirmation before implementing.'
  }
];

// ===========================================
// Render Task Brief as Markdown
// ===========================================
const renderTaskBriefMarkdown = (sections) => {
  let markdown = '';
  
  // Task section
  if (sections.task) {
    markdown += `# TASK\n${sections.task}\n\n`;
  }
  
  // Context section
  if (sections.contextFiles?.length || sections.contextFolders?.length || sections.contextDocs?.length) {
    markdown += `## CONTEXT TO USE\n`;
    sections.contextFiles?.forEach(f => f && (markdown += `- @Files: ${f}\n`));
    sections.contextFolders?.forEach(f => f && (markdown += `- @Folders: ${f}\n`));
    sections.contextDocs?.forEach(d => d && (markdown += `- @Docs: ${d}\n`));
    markdown += `\n`;
  }
  
  // Current behavior
  if (sections.currentBehavior) {
    markdown += `## CURRENT BEHAVIOR\n${sections.currentBehavior}\n\n`;
  }
  
  // Desired behavior
  if (sections.desiredBehavior) {
    markdown += `## DESIRED BEHAVIOR\n${sections.desiredBehavior}\n\n`;
  }
  
  // Scope section
  if (sections.inScope?.length || sections.outOfScope?.length) {
    markdown += `## SCOPE\n`;
    if (sections.inScope?.filter(s => s).length) {
      markdown += `IN SCOPE:\n`;
      sections.inScope.forEach(item => item && (markdown += `- ${item}\n`));
    }
    if (sections.outOfScope?.filter(s => s).length) {
      markdown += `\nOUT OF SCOPE / NON-GOALS:\n`;
      sections.outOfScope.forEach(item => item && (markdown += `- ${item}\n`));
    }
    markdown += `\n`;
  }
  
  // Constraints
  if (sections.constraints?.filter(c => c).length) {
    markdown += `## CONSTRAINTS\n`;
    sections.constraints.forEach(c => c && (markdown += `- ${c}\n`));
    markdown += `\n`;
  }
  
  // Acceptance criteria
  if (sections.acceptanceCriteria?.filter(ac => ac).length) {
    markdown += `## ACCEPTANCE CRITERIA\n`;
    sections.acceptanceCriteria.forEach(ac => ac && (markdown += `- [ ] ${ac}\n`));
    markdown += `\n`;
  }
  
  // Working style
  if (sections.workingStyle) {
    markdown += `## WORKING STYLE\n${sections.workingStyle}\n`;
  }
  
  return markdown.trim();
};

// ===========================================
// Task Brief Builder Component
// ===========================================
// Accepts optional initialSections prop to pre-populate form for editing existing prompts
const TaskBriefBuilder = ({ onComplete, onCancel, initialSections = null }) => {
  // Current step index
  const [currentStep, setCurrentStep] = useState(0);
  
  // ===========================================
  // Form State - Task Brief Sections
  // ===========================================
  // Use initialSections if provided (for editing), otherwise start with empty state
  const [sections, setSections] = useState(initialSections || {
    task: '',
    contextFiles: [''],
    contextFolders: [''],
    contextDocs: [''],
    currentBehavior: '',
    desiredBehavior: '',
    inScope: [''],
    outOfScope: [''],
    constraints: [''],
    acceptanceCriteria: [''],
    workingStyle: '',
    ideMode: 'any'
  });
  
  // ===========================================
  // Array Field Handlers
  // ===========================================
  const addArrayItem = (field) => {
    setSections(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };
  
  const updateArrayItem = (field, index, value) => {
    setSections(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };
  
  const removeArrayItem = (field, index) => {
    if (sections[field].length <= 1) return; // Keep at least one item
    setSections(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  
  // ===========================================
  // Navigation Handlers
  // ===========================================
  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const goToStep = (index) => {
    setCurrentStep(index);
  };
  
  // ===========================================
  // Complete Handler
  // ===========================================
  const handleComplete = () => {
    // Clean up empty array items
    const cleanedSections = {
      ...sections,
      contextFiles: sections.contextFiles.filter(f => f.trim()),
      contextFolders: sections.contextFolders.filter(f => f.trim()),
      contextDocs: sections.contextDocs.filter(d => d.trim()),
      inScope: sections.inScope.filter(s => s.trim()),
      outOfScope: sections.outOfScope.filter(s => s.trim()),
      constraints: sections.constraints.filter(c => c.trim()),
      acceptanceCriteria: sections.acceptanceCriteria.filter(ac => ac.trim())
    };
    
    const markdown = renderTaskBriefMarkdown(cleanedSections);
    
    onComplete({
      sections: cleanedSections,
      content: markdown,
      promptType: 'task_brief'
    });
  };
  
  // ===========================================
  // Apply Working Style Preset
  // ===========================================
  const applyPreset = (presetValue) => {
    setSections(prev => ({
      ...prev,
      workingStyle: presetValue
    }));
  };
  
  // ===========================================
  // Render Array Input Group
  // ===========================================
  const renderArrayInputs = (field, placeholder, Icon) => (
    <div className="task-brief-builder__array-group">
      {sections[field].map((item, index) => (
        <div key={index} className="task-brief-builder__array-item">
          {Icon && <Icon size={14} className="task-brief-builder__array-icon" />}
          <input
            type="text"
            value={item}
            onChange={(e) => updateArrayItem(field, index, e.target.value)}
            placeholder={placeholder}
            className="task-brief-builder__input"
          />
          {sections[field].length > 1 && (
            <button
              type="button"
              onClick={() => removeArrayItem(field, index)}
              className="task-brief-builder__remove-btn"
              title="Remove"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => addArrayItem(field)}
        className="task-brief-builder__add-btn"
      >
        <Plus size={14} />
        Add another
      </button>
    </div>
  );
  
  // ===========================================
  // Render Step Content
  // ===========================================
  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      // Step 1: Task
      case 'task':
        return (
          <div className="task-brief-builder__step-content">
            <h3>What's your goal?</h3>
            <p className="task-brief-builder__step-hint">
              Write a single, clear sentence describing what you want to accomplish.
            </p>
            <textarea
              value={sections.task}
              onChange={(e) => setSections(prev => ({ ...prev, task: e.target.value }))}
              placeholder="e.g., Add user authentication with JWT tokens and refresh token rotation"
              className="task-brief-builder__textarea"
              rows={3}
              maxLength={500}
              autoFocus
            />
            <div className="task-brief-builder__char-count">
              {sections.task.length} / 500
            </div>
          </div>
        );
      
      // Step 2: Context
      case 'context':
        return (
          <div className="task-brief-builder__step-content">
            <h3>What context should the AI use?</h3>
            <p className="task-brief-builder__step-hint">
              Specify files, folders, and documentation to attach with @mentions.
            </p>
            
            <div className="task-brief-builder__context-section">
              <label>@Files - Specific file paths</label>
              {renderArrayInputs('contextFiles', 'e.g., src/services/authService.js', FileText)}
            </div>
            
            <div className="task-brief-builder__context-section">
              <label>@Folders - Module directories</label>
              {renderArrayInputs('contextFolders', 'e.g., src/middleware/', FolderOpen)}
            </div>
            
            <div className="task-brief-builder__context-section">
              <label>@Docs - Documentation references</label>
              {renderArrayInputs('contextDocs', 'e.g., JWT Best Practices', BookOpen)}
            </div>
          </div>
        );
      
      // Step 3: Behavior
      case 'behavior':
        return (
          <div className="task-brief-builder__step-content">
            <h3>Current vs. Desired Behavior</h3>
            <p className="task-brief-builder__step-hint">
              Describe what happens now and what should happen instead.
            </p>
            
            <div className="task-brief-builder__behavior-section">
              <label>Current Behavior (what happens today)</label>
              <textarea
                value={sections.currentBehavior}
                onChange={(e) => setSections(prev => ({ ...prev, currentBehavior: e.target.value }))}
                placeholder="e.g., Users log in but tokens never expire, creating security risk"
                className="task-brief-builder__textarea"
                rows={4}
                maxLength={2000}
              />
            </div>
            
            <div className="task-brief-builder__behavior-section">
              <label>Desired Behavior (what should happen)</label>
              <textarea
                value={sections.desiredBehavior}
                onChange={(e) => setSections(prev => ({ ...prev, desiredBehavior: e.target.value }))}
                placeholder="e.g., Access tokens expire in 15 minutes, refresh tokens in 7 days with rotation"
                className="task-brief-builder__textarea"
                rows={4}
                maxLength={2000}
              />
            </div>
          </div>
        );
      
      // Step 4: Scope
      case 'scope':
        return (
          <div className="task-brief-builder__step-content">
            <h3>Define the Scope</h3>
            <p className="task-brief-builder__step-hint">
              Be explicit about what's in scope and what should NOT be touched.
            </p>
            
            <div className="task-brief-builder__scope-section">
              <label className="task-brief-builder__label--in">IN SCOPE - What to change</label>
              {renderArrayInputs('inScope', 'e.g., Add token refresh endpoint', Check)}
            </div>
            
            <div className="task-brief-builder__scope-section">
              <label className="task-brief-builder__label--out">OUT OF SCOPE / NON-GOALS - What NOT to touch</label>
              {renderArrayInputs('outOfScope', 'e.g., Don\'t change user registration flow', X)}
            </div>
          </div>
        );
      
      // Step 5: Constraints
      case 'constraints':
        return (
          <div className="task-brief-builder__step-content">
            <h3>Set Constraints</h3>
            <p className="task-brief-builder__step-hint">
              Define boundaries: tech, style, safety, and performance constraints.
            </p>
            {renderArrayInputs('constraints', 'e.g., Use existing bcrypt for password hashing', Shield)}
            
            <div className="task-brief-builder__constraint-hints">
              <span className="task-brief-builder__constraint-hint">💻 Tech: frameworks, versions, no new deps</span>
              <span className="task-brief-builder__constraint-hint">🎨 Style: lint rules, patterns to follow</span>
              <span className="task-brief-builder__constraint-hint">🔒 Safety: don't touch secrets, no destructive commands</span>
              <span className="task-brief-builder__constraint-hint">⚡ Performance: latency/memory expectations</span>
            </div>
          </div>
        );
      
      // Step 6: Acceptance Criteria
      case 'acceptance':
        return (
          <div className="task-brief-builder__step-content">
            <h3>Acceptance Criteria</h3>
            <p className="task-brief-builder__step-hint">
              Define testable conditions that indicate success. These become checkboxes.
            </p>
            {renderArrayInputs('acceptanceCriteria', 'e.g., Access token expires after 15 minutes', CheckSquare)}
            
            <div className="task-brief-builder__working-style">
              <label>Working Style</label>
              <textarea
                value={sections.workingStyle}
                onChange={(e) => setSections(prev => ({ ...prev, workingStyle: e.target.value }))}
                placeholder="How should the AI approach this task? (plan-first, minimal diffs, etc.)"
                className="task-brief-builder__textarea"
                rows={4}
                maxLength={500}
              />
              <div className="task-brief-builder__presets">
                <span>Presets:</span>
                {WORKING_STYLE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => applyPreset(preset.value)}
                    className="task-brief-builder__preset-btn"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="task-brief-builder__ide-mode">
              <label>Recommended IDE Mode</label>
              <div className="task-brief-builder__mode-options">
                {IDE_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setSections(prev => ({ ...prev, ideMode: mode.value }))}
                    className={`task-brief-builder__mode-btn ${sections.ideMode === mode.value ? 'active' : ''}`}
                    title={mode.description}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      
      // Step 7: Review
      case 'review':
        const markdown = renderTaskBriefMarkdown(sections);
        return (
          <div className="task-brief-builder__step-content">
            <h3>Review Your Task Brief</h3>
            <p className="task-brief-builder__step-hint">
              Preview the generated markdown. Click "Create" to use this prompt.
            </p>
            
            <div className="task-brief-builder__preview">
              <pre>{markdown || 'Fill in at least the Task field to generate a brief.'}</pre>
            </div>
            
            <button
              type="button"
              className="task-brief-builder__copy-btn"
              onClick={() => navigator.clipboard.writeText(markdown)}
              disabled={!markdown}
            >
              <Clipboard size={14} />
              Copy to Clipboard
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  // ===========================================
  // Render
  // ===========================================
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const hasMinimumContent = sections.task.trim().length > 0;
  
  return (
    <div className="task-brief-builder">
      {/* ===========================================
          Step Indicator
      =========================================== */}
      <div className="task-brief-builder__steps">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          
          return (
            <button
              key={step.id}
              className={`task-brief-builder__step-indicator ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              onClick={() => goToStep(index)}
              title={`${step.label}: ${step.description}`}
            >
              <StepIcon size={16} />
              <span className="task-brief-builder__step-label">{step.label}</span>
            </button>
          );
        })}
      </div>
      
      {/* ===========================================
          Step Content
      =========================================== */}
      {renderStepContent()}
      
      {/* ===========================================
          Navigation
      =========================================== */}
      <div className="task-brief-builder__nav">
        <button
          type="button"
          onClick={onCancel}
          className="task-brief-builder__nav-btn task-brief-builder__nav-btn--cancel"
        >
          Cancel
        </button>
        
        <div className="task-brief-builder__nav-arrows">
          <button
            type="button"
            onClick={goPrev}
            disabled={isFirstStep}
            className="task-brief-builder__nav-btn task-brief-builder__nav-btn--prev"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          
          {isLastStep ? (
            <button
              type="button"
              onClick={handleComplete}
              disabled={!hasMinimumContent}
              className="task-brief-builder__nav-btn task-brief-builder__nav-btn--complete"
            >
              <Check size={16} />
              Create Task Brief
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="task-brief-builder__nav-btn task-brief-builder__nav-btn--next"
            >
              Next
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskBriefBuilder;

